/**
 * Centralized deny (tooltip / title) messages for permission UI (Phase IW3.12.A).
 * Use for PermissionButton denyMessage, IncidentStatusControl disabledTooltip,
 * DetailDrawerShell editDenyMessage, submit errors, etc.
 * Override at call site when business rule needs a different message.
 */

import { MENU_PATHS } from './permissions';

// ----- Incidents -----
export const DENY_INCIDENT_CREATE = 'Bạn không có quyền tạo sự cố';
export const DENY_INCIDENT_EDIT_CREATOR = 'Chỉ người tạo hoặc Quản trị viên mới được chỉnh sửa sự cố';
export const DENY_INCIDENT_EDIT_CREATOR_FORM = 'Chỉ người tạo hoặc Quản trị viên mới được sửa sự cố này';
// IW3.14.1: Admin-only override (System Leader removed)
export const DENY_INCIDENT_DELETE_ADMIN_LEADER = 'Chỉ Quản trị viên mới được xóa sự cố';
export const DENY_INCIDENT_CLOSE_ADMIN_LEADER = 'Chỉ Quản trị viên mới được đóng sự cố';
export const DENY_INCIDENT_STATUS_CLOSED_ADMIN_LEADER =
  'Chỉ Quản trị viên mới được thay đổi trạng thái của sự cố đã đóng';
export const DENY_INCIDENT_CHANGE_STATUS = 'Bạn không có quyền đổi trạng thái sự cố';
export const DENY_INCIDENT_CLOSED_CANNOT_EDIT = 'Sự cố đã đóng, không thể chỉnh sửa';
export const DENY_INCIDENT_UPDATE = 'Bạn không có quyền sửa sự cố';

// ----- Work Reports -----
export const DENY_WORK_REPORT_CREATE = 'Bạn không có quyền tạo báo cáo';
export const DENY_WORK_REPORT_UPDATE = 'Bạn không có quyền sửa báo cáo';
export const DENY_WORK_REPORT_DELETE = 'Bạn không có quyền xóa báo cáo';
export const DENY_WORK_REPORT_EDIT_ASSIGNEE = 'Chỉ người được phân công hoặc Quản trị viên mới được sửa báo cáo';
export const DENY_WORK_REPORT_CREATE_SUBMIT = 'Bạn không có quyền tạo báo cáo công việc';
export const DENY_WORK_REPORT_UPDATE_SUBMIT = 'Bạn không có quyền cập nhật báo cáo công việc';
export const DENY_WORK_REPORT_EDIT_SUBMIT = 'Bạn không có quyền sửa báo cáo công việc';

// ----- Detail drawer (generic) -----
export const DENY_DETAIL_EDIT_DEFAULT = 'Bạn không có quyền sửa';

// ----- Accounts -----
export const DENY_ACCOUNTS_UPDATE = 'Bạn không có quyền sửa tài khoản';
export const DENY_ACCOUNTS_RESET_PASSWORD = 'Bạn không có quyền cấp lại mật khẩu';
export const DENY_ACCOUNTS_UNLOCK_RATE_LIMIT = 'Bạn không có quyền mở khoá giới hạn đăng nhập';
export const DENY_ACCOUNTS_DISABLE_2FA = 'Bạn không có quyền tắt xác thực 2 lớp';
export const DENY_ACCOUNTS_LOCK = 'Bạn không có quyền khoá tài khoản';
export const DENY_ACCOUNTS_UNLOCK = 'Bạn không có quyền mở khoá tài khoản';
export const DENY_ACCOUNTS_DELETE = 'Bạn không có quyền xóa tài khoản';
export const DENY_ACCOUNTS_CREATE = 'Bạn không có quyền tạo tài khoản';

// ----- Roles -----
export const DENY_ROLES_CREATE = 'Bạn không có quyền thêm vai trò';
export const DENY_ROLES_DELETE = 'Bạn không có quyền xóa vai trò';
export const DENY_ROLES_UPDATE = 'Bạn không có quyền sửa vai trò';
export const DENY_ROLES_ASSIGN = 'Bạn không có quyền gán người dùng';
export const DENY_ROLES_REVOKE = 'Bạn không có quyền thu hồi';
export const DENY_ROLES_GRANT = 'Bạn không có quyền cấp quyền';

// ----- Delegations (IW3.14.3.1) -----
export const DENY_DELEGATION_GRANT = 'Bạn không có quyền cấp quyền sửa/xoá dữ liệu người khác';
export const DENY_DELEGATION_REVOKE = 'Bạn không có quyền thu hồi quyền sửa/xoá dữ liệu';
/** IW3.15.A: Sửa delegation cần cả create + delete */
export const DENY_DELEGATION_EDIT = 'Bạn cần cả quyền cấp và thu hồi để sửa quyền sửa/xoá dữ liệu';

// ----- Projects list -----
export const DENY_PROJECTS_LIST_UPDATE = 'Bạn không có quyền sửa dự án';
export const DENY_PROJECTS_LIST_DELETE = 'Bạn không có quyền xóa dự án';
export const DENY_PROJECTS_LIST_CREATE = 'Bạn không có quyền thêm dự án';

// ----- Projects assignment -----
export const DENY_PROJECTS_ASSIGNMENT_DELETE = 'Bạn không có quyền xóa phân chia';
export const DENY_PROJECTS_ASSIGNMENT_CREATE = 'Bạn không có quyền phân chia';

// ----- Duty schedules -----
export const DENY_DUTY_SCHEDULES_CREATE = 'Bạn không có quyền phân chia trực';
export const DENY_DUTY_SCHEDULES_UPDATE = 'Bạn không có quyền cập nhật';
export const DENY_DUTY_SCHEDULES_DELETE = 'Bạn không có quyền xóa';

// ----- Incidents config (KPI policies) -----
export const DENY_INCIDENTS_CONFIG_UPDATE = 'Bạn không có quyền chỉnh sửa KPI policy';
export const DENY_INCIDENTS_CONFIG_DELETE = 'Bạn không có quyền xóa KPI policy';
export const DENY_INCIDENTS_CONFIG_CREATE = 'Bạn không có quyền tạo KPI policy';

export const DENY_OVERVIEW_WIDGET_EDIT = 'Bạn không có quyền cấu hình widget Tổng quan';

// ----- Default deny by (menuPath, action) for useActionPermission (Phase IW3.12.B) -----
type CrudActionForDeny = 'view' | 'create' | 'update' | 'delete';

const DEFAULT_DENY_MAP: Record<string, Record<CrudActionForDeny, string>> = {
  [MENU_PATHS.OVERVIEW_WIDGET]: {
    view: DENY_OVERVIEW_WIDGET_EDIT,
    create: DENY_OVERVIEW_WIDGET_EDIT,
    update: DENY_OVERVIEW_WIDGET_EDIT,
    delete: DENY_OVERVIEW_WIDGET_EDIT,
  },
  [MENU_PATHS.INCIDENTS]: {
    view: DENY_INCIDENT_UPDATE,
    create: DENY_INCIDENT_CREATE,
    update: DENY_INCIDENT_UPDATE,
    delete: DENY_INCIDENT_DELETE_ADMIN_LEADER,
  },
  [MENU_PATHS.WORK_REPORTS]: {
    view: DENY_WORK_REPORT_UPDATE,
    create: DENY_WORK_REPORT_CREATE,
    update: DENY_WORK_REPORT_UPDATE,
    delete: DENY_WORK_REPORT_DELETE,
  },
  [MENU_PATHS.PROJECTS_LIST]: {
    view: DENY_PROJECTS_LIST_UPDATE,
    create: DENY_PROJECTS_LIST_CREATE,
    update: DENY_PROJECTS_LIST_UPDATE,
    delete: DENY_PROJECTS_LIST_DELETE,
  },
  [MENU_PATHS.PROJECTS_ASSIGNMENT]: {
    view: DENY_PROJECTS_ASSIGNMENT_CREATE,
    create: DENY_PROJECTS_ASSIGNMENT_CREATE,
    update: DENY_PROJECTS_ASSIGNMENT_CREATE,
    delete: DENY_PROJECTS_ASSIGNMENT_DELETE,
  },
  [MENU_PATHS.PROJECTS_DUTY_SCHEDULES]: {
    view: DENY_DUTY_SCHEDULES_UPDATE,
    create: DENY_DUTY_SCHEDULES_CREATE,
    update: DENY_DUTY_SCHEDULES_UPDATE,
    delete: DENY_DUTY_SCHEDULES_DELETE,
  },
  [MENU_PATHS.PERMISSIONS_ACCOUNTS]: {
    view: DENY_ACCOUNTS_UPDATE,
    create: DENY_ACCOUNTS_CREATE,
    update: DENY_ACCOUNTS_UPDATE,
    delete: DENY_ACCOUNTS_DELETE,
  },
  [MENU_PATHS.PERMISSIONS_ROLES]: {
    view: DENY_ROLES_UPDATE,
    create: DENY_ROLES_CREATE,
    update: DENY_ROLES_UPDATE,
    delete: DENY_ROLES_DELETE,
  },
  [MENU_PATHS.PERMISSIONS_DELEGATIONS]: {
    view: DENY_DELEGATION_GRANT,
    create: DENY_DELEGATION_GRANT,
    update: DENY_DELEGATION_GRANT,
    delete: DENY_DELEGATION_REVOKE,
  },
  [MENU_PATHS.INCIDENTS_CONFIG]: {
    view: DENY_INCIDENTS_CONFIG_UPDATE,
    create: DENY_INCIDENTS_CONFIG_CREATE,
    update: DENY_INCIDENTS_CONFIG_UPDATE,
    delete: DENY_INCIDENTS_CONFIG_DELETE,
  },
};

const FALLBACK_DENY = 'Bạn không có quyền thực hiện thao tác này';

/**
 * Returns the default deny message for (menuPath, action).
 * Used by useActionPermission hook (Phase IW3.12.B).
 */
export function getDefaultDenyMessage(menuPath: string, action: CrudActionForDeny): string {
  const byPath = DEFAULT_DENY_MAP[menuPath];
  if (!byPath) return FALLBACK_DENY;
  const msg = byPath[action];
  return msg ?? FALLBACK_DENY;
}
